import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  Cloud,
  File,
  FileText,
  Folder,
  Home,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { INDEXABLE_EXTENSIONS } from "@/constants/files";
import { googleDriveService } from "@/lib/services";
import type { GoogleDriveItem } from "@/types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SUPPORTED_EXTENSIONS = INDEXABLE_EXTENSIONS;

/** Google Workspace MIME types that can be exported to supported formats. */
const WORKSPACE_MIME_TYPES = new Set([
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.drawing",
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoogleDriveSelection {
  items: GoogleDriveItem[];
}

interface BreadcrumbEntry {
  label: string;
  folderId?: string; // undefined = root
}

interface GoogleDriveBrowserProps {
  onSelect: (selection: GoogleDriveSelection) => void;
  onCancel: () => void;
  multiSelect?: boolean;
  filesOnly?: boolean;
  title?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getFileIcon(item: GoogleDriveItem) {
  if (item.is_folder) return Folder;
  const ext = item.name.split(".").pop()?.toLowerCase();
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(ext || "")) return FileText;
  return File;
}

function isSupportedFile(item: GoogleDriveItem): boolean {
  if (item.is_folder) return true;
  if (item.mime_type && WORKSPACE_MIME_TYPES.has(item.mime_type)) return true;
  const dot = item.name.lastIndexOf(".");
  if (dot === -1) return false;
  return SUPPORTED_EXTENSIONS.has(item.name.slice(dot).toLowerCase());
}

function getWorkspaceTypeLabel(mimeType?: string): string | null {
  if (!mimeType) return null;
  const labels: Record<string, string> = {
    "application/vnd.google-apps.document": "Google Doc",
    "application/vnd.google-apps.spreadsheet": "Google Sheet",
    "application/vnd.google-apps.presentation": "Google Slides",
    "application/vnd.google-apps.drawing": "Google Drawing",
  };
  return labels[mimeType] ?? null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GoogleDriveBrowser({
  onSelect,
  onCancel,
  multiSelect = true,
  filesOnly = false,
  title,
}: Readonly<GoogleDriveBrowserProps>) {
  const [items, setItems] = useState<GoogleDriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([{ label: "My Drive" }]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Current folder ID (undefined = root)
  const currentFolderId = breadcrumbs[breadcrumbs.length - 1]?.folderId;

  // --- Data loading ---

  const loadFolder = useCallback(async (folderId?: string) => {
    setLoading(true);
    setError(null);
    setIsSearching(false);
    setSearchQuery("");
    try {
      const response = await googleDriveService.listFiles(folderId);
      setItems(response.items);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load files";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    setIsSearching(true);
    try {
      const results = await googleDriveService.searchFiles(searchQuery.trim());
      setItems(results);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Search failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Load root on mount
  useEffect(() => {
    loadFolder();
  }, [loadFolder]);

  // --- Navigation ---

  const navigateToFolder = useCallback(
    (folder: GoogleDriveItem) => {
      setBreadcrumbs((prev) => [...prev, { label: folder.name, folderId: folder.id }]);
      setSelectedIds(new Set());
      loadFolder(folder.id);
    },
    [loadFolder],
  );

  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setSelectedIds(new Set());
      loadFolder(newBreadcrumbs[newBreadcrumbs.length - 1]?.folderId);
    },
    [breadcrumbs, loadFolder],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setIsSearching(false);
    loadFolder(currentFolderId);
  }, [currentFolderId, loadFolder]);

  // --- Selection ---

  const toggleSelection = useCallback(
    (item: GoogleDriveItem) => {
      if (item.is_folder && filesOnly) return;
      if (!isSupportedFile(item)) return;

      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          if (!multiSelect) next.clear();
          next.add(item.id);
        }
        return next;
      });
    },
    [filesOnly, multiSelect],
  );

  const handleConfirm = useCallback(() => {
    const selected = items.filter((i) => selectedIds.has(i.id));
    if (selected.length === 0) return;
    onSelect({ items: selected });
  }, [items, selectedIds, onSelect]);

  // --- Render ---

  const displayItems = items;
  const selectedCount = selectedIds.size;

  return (
    <div className="flex flex-col h-full max-h-[85vh]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
        <div className="flex items-center gap-2">
          <Cloud className="w-5 h-5 text-brand-primary" />
          <h2 className="text-lg font-semibold text-content-primary">{title || "Google Drive"}</h2>
        </div>
        <button onClick={onCancel} className="p-1 hover:bg-surface-secondary rounded-lg transition">
          <X className="w-5 h-5 text-content-tertiary" />
        </button>
      </div>

      {/* Breadcrumbs */}
      {!isSearching && (
        <div className="flex items-center gap-1 px-4 py-2 text-sm text-content-secondary overflow-x-auto border-b border-border-primary">
          {breadcrumbs.map((bc, i) => (
            <span key={`${bc.label}-${i}`} className="flex items-center gap-1 shrink-0">
              {i > 0 && <ChevronRight className="w-3 h-3 text-content-quaternary" />}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                className={`hover:text-brand-primary transition ${
                  i === breadcrumbs.length - 1 ? "text-content-primary font-medium" : ""
                }`}
              >
                {i === 0 ? <Home className="w-4 h-4" /> : bc.label}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="px-4 py-2 border-b border-border-primary">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-quaternary" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
              if (e.key === "Escape" && isSearching) clearSearch();
            }}
            placeholder="Search files..."
            className="w-full pl-9 pr-8 py-2 text-sm bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary"
          />
          {isSearching && (
            <button
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-surface-tertiary rounded"
            >
              <X className="w-4 h-4 text-content-tertiary" />
            </button>
          )}
        </div>
        {isSearching && (
          <div className="mt-1 flex items-center gap-2">
            <button
              onClick={clearSearch}
              className="flex items-center gap-1 text-xs text-brand-primary hover:underline"
            >
              <ArrowLeft className="w-3 h-3" /> Back to browsing
            </button>
            <span className="text-xs text-content-quaternary">
              {items.length} result{items.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* File list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
            <span className="ml-2 text-content-secondary text-sm">Loading...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <AlertCircle className="w-8 h-8 text-status-error mb-2" />
            <p className="text-sm text-status-error text-center">{error}</p>
            <button
              onClick={() => loadFolder(currentFolderId)}
              className="mt-3 text-sm text-brand-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-content-quaternary">
            <Folder className="w-8 h-8 mb-2" />
            <p className="text-sm">{isSearching ? "No files found" : "This folder is empty"}</p>
          </div>
        ) : (
          <div className="divide-y divide-border-primary">
            {displayItems.map((item) => {
              const Icon = getFileIcon(item);
              const supported = isSupportedFile(item);
              const isSelected = selectedIds.has(item.id);
              const selectable = item.is_folder ? !filesOnly && supported : supported;
              const workspaceLabel = getWorkspaceTypeLabel(item.mime_type ?? undefined);

              return (
                <button
                  type="button"
                  key={item.id}
                  className={`flex items-center gap-3 px-4 py-2.5 transition cursor-pointer w-full text-left ${
                    isSelected ? "bg-brand-primary/10" : "hover:bg-surface-secondary"
                  } ${!supported && !item.is_folder ? "opacity-50" : ""}`}
                  onClick={() => {
                    if (item.is_folder) {
                      navigateToFolder(item);
                    } else if (selectable) {
                      toggleSelection(item);
                    }
                  }}
                >
                  {/* Selection checkbox (files only, or folders if allowed) */}
                  {selectable && !item.is_folder ? (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      className="w-5 h-5 rounded border-2 shrink-0 accent-brand-primary cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(item);
                      }}
                      onChange={() => {
                        /* handled by onClick to stopPropagation */
                      }}
                    />
                  ) : (
                    <div className="w-5 h-5 shrink-0" />
                  )}

                  {/* Icon */}
                  <Icon
                    className={`w-5 h-5 shrink-0 ${item.is_folder ? "text-brand-primary" : "text-content-tertiary"}`}
                  />

                  {/* Name + meta */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-content-primary truncate">{item.name}</div>
                    <div className="flex items-center gap-2 text-xs text-content-quaternary">
                      {workspaceLabel && <span>{workspaceLabel}</span>}
                      {item.size != null && <span>{formatFileSize(item.size)}</span>}
                      {item.last_modified && <span>{new Date(item.last_modified).toLocaleDateString()}</span>}
                      {!supported && !item.is_folder && <span className="text-status-warning">Unsupported format</span>}
                    </div>
                  </div>

                  {/* Folder chevron */}
                  {item.is_folder && <ChevronRight className="w-4 h-4 text-content-quaternary shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border-primary bg-surface-primary">
        <span className="text-sm text-content-secondary">
          {selectedCount > 0
            ? `${selectedCount} file${selectedCount !== 1 ? "s" : ""} selected`
            : "Select files to add"}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary transition"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedCount === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add {selectedCount > 0 ? `(${selectedCount})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
